import { addMinutes, format, parse, isSameDay, addDays } from 'date-fns';

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  type?: 'workout' | 'meeting' | 'break';
}

export interface TimeSlot {
  start: Date;
  end: Date;
  duration: number;
  isPreferred?: boolean;
}

interface CalendarPreferences {
  earliestTime: string; // "HH:mm" format
  latestTime: string;  // "HH:mm" format
  minimumGap: number;  // minutes between events
  preferredTimes?: string[]; // "HH:mm" format
  blackoutPeriods?: Array<{
    start: string;
    end: string;
    days?: number[]; // 0-6, where 0 is Sunday
  }>;
}

const workoutTypes = [
  'HIIT Training',
  'Strength Session',
  'Recovery Workout',
  'Cardio Session',
  'Mobility Work'
];

// Mock calendar integration - replace with actual calendar API in production
export async function getCalendarEvents(startDate: Date, endDate: Date): Promise<CalendarEvent[]> {
  // Generate realistic mock events
  const events: CalendarEvent[] = [];
  let currentDate = startDate;

  while (currentDate <= endDate) {
    // Add scheduled workout (7-8 AM) on MWF
    if ([1, 3, 5].includes(currentDate.getDay())) {
      events.push({
        id: `workout-${currentDate.toISOString()}`,
        title: `${workoutTypes[Math.floor(Math.random() * workoutTypes.length)]}`,
        start: new Date(currentDate.setHours(7, 0, 0)),
        end: new Date(currentDate.setHours(8, 0, 0)),
        type: 'workout'
      });
    }

    // Add morning meeting (9-10 AM) on weekdays
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      events.push({
        id: `meeting-${currentDate.toISOString()}`,
        title: 'Morning Meeting',
        start: new Date(currentDate.setHours(9, 0, 0)),
        end: new Date(currentDate.setHours(10, 0, 0)),
        type: 'meeting'
      });
    }

    // Add lunch break (12-1 PM)
    events.push({
      id: `lunch-${currentDate.toISOString()}`,
      title: 'Lunch Break',
      start: new Date(currentDate.setHours(12, 0, 0)),
      end: new Date(currentDate.setHours(13, 0, 0)),
      type: 'break'
    });

    // Add afternoon meeting (2-3 PM) on weekdays
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      events.push({
        id: `afternoon-${currentDate.toISOString()}`,
        title: 'Team Sync',
        start: new Date(currentDate.setHours(14, 0, 0)),
        end: new Date(currentDate.setHours(15, 0, 0)),
        type: 'meeting'
      });
    }

    // Add evening workout (5-6 PM) on TTS
    if ([2, 4, 6].includes(currentDate.getDay())) {
      events.push({
        id: `workout-evening-${currentDate.toISOString()}`,
        title: `${workoutTypes[Math.floor(Math.random() * workoutTypes.length)]}`,
        start: new Date(currentDate.setHours(17, 0, 0)),
        end: new Date(currentDate.setHours(18, 0, 0)),
        type: 'workout'
      });
    }

    currentDate = addDays(currentDate, 1);
  }

  return events;
}

export function findAvailableTimeSlots(
  events: CalendarEvent[],
  date: Date,
  duration: number,
  preferences: CalendarPreferences
): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const dayStart = parse(preferences.earliestTime, 'HH:mm', date);
  const dayEnd = parse(preferences.latestTime, 'HH:mm', date);
  
  // Sort events chronologically
  const sortedEvents = [...events]
    .filter(event => isSameDay(event.start, date))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  
  let currentTime = dayStart;
  
  // Check each potential slot
  sortedEvents.forEach((event) => {
    const gap = (event.start.getTime() - currentTime.getTime()) / (1000 * 60);
    
    if (gap >= duration + preferences.minimumGap) {
      // Check if this slot overlaps with blackout periods
      const isBlackout = preferences.blackoutPeriods?.some(period => {
        const periodStart = parse(period.start, 'HH:mm', date);
        const periodEnd = parse(period.end, 'HH:mm', date);
        const days = period.days || [0, 1, 2, 3, 4, 5, 6];
        return (
          days.includes(date.getDay()) &&
          currentTime >= periodStart && 
          currentTime <= periodEnd
        );
      });

      if (!isBlackout) {
        const isPreferred = preferences.preferredTimes?.includes(
          format(currentTime, 'HH:mm')
        );

        slots.push({
          start: currentTime,
          end: addMinutes(currentTime, duration),
          duration,
          isPreferred
        });
      }
    }
    
    currentTime = event.end;
  });
  
  // Check final slot after last event
  const finalGap = (dayEnd.getTime() - currentTime.getTime()) / (1000 * 60);
  if (finalGap >= duration + preferences.minimumGap) {
    const isPreferred = preferences.preferredTimes?.includes(
      format(currentTime, 'HH:mm')
    );

    slots.push({
      start: currentTime,
      end: addMinutes(currentTime, duration),
      duration,
      isPreferred
    });
  }
  
  return slots;
}

export async function addWorkoutToCalendar(
  workout: {
    name: string;
    duration: number;
  },
  timeSlot: TimeSlot,
  recurrence?: {
    frequency: 'weekly' | 'biweekly';
    weeks: number;
  }
): Promise<boolean> {
  try {
    // Mock calendar integration - replace with actual calendar API
    console.log('Adding workout to calendar:', {
      title: workout.name,
      start: format(timeSlot.start, 'HH:mm'),
      end: format(timeSlot.end, 'HH:mm'),
      duration: workout.duration,
      recurrence
    });

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return true;
  } catch (error) {
    console.error('Error adding workout to calendar:', error);
    return false;
  }
}